import React from 'react';
import {
    KebabFilled,
    TrashOutlined,
    Divider,
    DropdownMenu,
    DownloadOutlined,
    IconButton,
    SharingOutlined,
    AddToCollectionOutlined,
    ListGroup,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { CreatorDownloadSource, CreatorCollectionPlaybackMeta } from 'types';
import useShareCreatorButton from 'components/Creator/ShareCreator/useShareCreatorButton';
import { usePrivateActionClickHandler } from 'hooks/usePrivateActionClickHandler/usePrivateActionClickHandler';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import useAddSampleToCollectionButton from 'components/CommonUi/AddSampleToCollectionButton/useAddSampleToCollectionButton';
import { useOpenAddToCollectionPrompt } from 'actions/UserCollection';
import useRemoveFromCreatorButton from 'components/Creator/RemoveFromCreatorButton/useRemoveFromCreatorButton';
import { Sample } from 'types/generated/graphql';
import { getCollectionPlaceholder } from '../CreatorHeader.helpers';
import { useClickHandlers } from './CreatorHeaderMobileMenu.hooks';

export interface CreatorHeaderMobileMenuProps {
    samples: Sample[];
    creatorDownloadSource: CreatorDownloadSource;
    playbackMeta?: CreatorCollectionPlaybackMeta;
    isDisabled: boolean;
}

export interface ActionsGroupProps extends CreatorHeaderMobileMenuProps {
    hide: () => void;
}

const ActionsGroup = ({
    samples,
    creatorDownloadSource,
    playbackMeta,
    isDisabled,
    hide,
}: ActionsGroupProps): JSX.Element => {
    // CONTEXT
    const { dispatch, openShareModal } = useCreatorContext();
    const purchaseController = usePurchaseControllerContext();

    const {
        handleShareClick,
        handleDownloadFullTrackClick,
        handleDownloadSamplesClick,
        handleAddSamplesToCollectionClick,
    } = useClickHandlers({
        hide,
        openShareModal,
        purchaseController,
        creatorDownloadSource,
        dispatch,
    });

    const { anchorRef, openAddToCollection, AddToCollection } =
        useOpenAddToCollectionPrompt();

    // use buttons hooks
    const { handleClick: shareButtonClick } = useShareCreatorButton({
        onClick: handleShareClick,
    });

    const handleDownloadButtonClick = usePrivateActionClickHandler({
        authOnly: true,
        onClick: shareButtonClick,
        userAction: {
            type: '',
            description: t`share your track`,
            args: [],
        },
    });

    const placeholder = getCollectionPlaceholder(playbackMeta);

    const { handleOnClick: handleAddToCollectionClick } =
        useAddSampleToCollectionButton({
            onClick: handleAddSamplesToCollectionClick,
            samples: samples,
            source: { interaction: 'Creator' },
            hide,
            openAddToCollection,
            placeholder,
        });

    const { handleClick: handleRemoveFromCreatorClick } =
        useRemoveFromCreatorButton({ onClick: hide });

    const showDownloadActions = samples.length > 0;

    return (
        <ListGroup pt="md" pb="md">
            {showDownloadActions && (
                <>
                    <ListGroup.Item
                        isDisabled={isDisabled}
                        onClick={handleDownloadFullTrackClick}
                        icon={<DownloadOutlined />}
                        label={t`Download full track`}
                        aria-label={t`Download full track`}
                        description={t`Download your mixdown`}
                    />
                    <ListGroup.Item
                        isDisabled={isDisabled}
                        onClick={handleDownloadSamplesClick}
                        icon={<DownloadOutlined />}
                        aria-label={t`Download samples`}
                        label={t`Download samples`}
                        description={t`Your processed samples`}
                    />
                </>
            )}
            <Divider />
            <ListGroup.Item
                isDisabled={isDisabled}
                onClick={handleDownloadButtonClick}
                icon={<SharingOutlined />}
                aria-label={t`Share your track`}
                label={t`Share your track`}
            />
            <Divider />
            <>
                <ListGroup.Item
                    onClick={handleAddToCollectionClick}
                    isDisabled={isDisabled}
                    icon={<AddToCollectionOutlined />}
                    label={t`Add to my collection`}
                />
                <AddToCollection samples={samples} anchorRef={anchorRef} />
            </>
            <ListGroup.Item
                onClick={handleRemoveFromCreatorClick}
                icon={<TrashOutlined />}
                label={t`Delete`}
            />
        </ListGroup>
    );
};

export const CreatorHeaderMobileMenu = ({
    ...props
}: CreatorHeaderMobileMenuProps): JSX.Element => (
    <DropdownMenu placement="bottom-end">
        {({ isOpen, close }): JSX.Element => (
            <>
                <DropdownMenu.Trigger>
                    <IconButton
                        data-component="CreatorHeaderMobileMenu"
                        icon={KebabFilled}
                        variant="secondary"
                        isRounded
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        aria-label={t`View more`}
                        size="sm"
                        label={t`More`}
                    />
                </DropdownMenu.Trigger>
                <DropdownMenu.List py="none" px="none">
                    <ActionsGroup {...props} hide={close} />
                </DropdownMenu.List>
            </>
        )}
    </DropdownMenu>
);
